import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef
} from 'react';
import {
  Avatar,
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Typography,
  TextField,
  IconButton,
  Alert
} from '@mui/material';
import { IconReload, IconBuildings, IconChevronDown, IconChevronUp, IconWorld } from '@tabler/icons-react';
import { alpha } from '@mui/material/styles';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // Using ArrowBackIcon for the left arrow
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import TruncateTooltip from '../../../commonComponent/TruncateTooltip';
import { FormattedMessage, useIntl } from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import DeviceGroupIcon from '../../../assets/images/device-group-icon.png';
import DarkIcon from '../../../assets/images/icons/building-darkTheme.svg';
import LightIcon from '../../../assets/images/icons/building-icon.svg';
import { useGetMenuMaster } from 'api/menu';
import useConfig from 'hooks/useConfig';
import { ThemeMode } from 'config';
import VirtualOrgHierarchyTree from './VirtualOrgHierarchyTree';
import { useNavigate } from 'react-router-dom';
import { setGlobalSearchSsidHandoff } from 'utils/CommonMethods';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
const toExpandedItemIds = (ids) =>
  (ids || []).filter((id) => id != null && id !== '').map((id) => String(id));

/** Stable key for expand/collapse + virtual rows (`treeRowKey`); falls back to `id` for trees without keys. */
const treeNodeExpandId = (node) =>
  String(node?.treeRowKey != null && node.treeRowKey !== '' ? node.treeRowKey : node?.id ?? '');

// Filter tree data: keep nodes whose label matches or that have a matching descendant.
// When a node matches, keep its full subtree (all nested children) so search surfaces the whole branch.
// When it does not match, only keep children that match or contain a match further down.
const filterTreeData = (nodes, queryLower) => {
  if (!queryLower) return nodes;

  return nodes.reduce((acc, node) => {
    const matches = node.label.toLowerCase().includes(queryLower);

    if (matches) {
      const fullChildren = Array.isArray(node.children) ? node.children : [];
      return [...acc, { ...node, children: fullChildren }];
    }

    if (node.children && node.children.length > 0) {
      const filteredChildren = filterTreeData(node.children, queryLower);
      if (filteredChildren.length > 0) {
        return [...acc, { ...node, children: filteredChildren }];
      }
    }
    return acc;
  }, []);
};

// Find the path to the node with the given label
const findNodePath = (nodes, label, path = []) => {
  for (const node of nodes) {
    if (node.label === label) {
      return [...path, treeNodeExpandId(node)];
    }
    if (node.children && node.children.length > 0) {
      const childPath = findNodePath(node.children, label, [...path, treeNodeExpandId(node)]);
      if (childPath.length > 0) {
        return childPath;
      }
    }
  }
  return [];
};

// Find the path to the node with the given label and type (type-aware match)
const findNodePathByLabelAndType = (nodes, label, type, path = []) => {
  for (const node of nodes) {
    const labelMatches = node.label === label;
    const typeMatches = !type || node.type === type;
    if (labelMatches && typeMatches) {
      return [...path, treeNodeExpandId(node)];
    }
    if (node.children && node.children.length > 0) {
      const childPath = findNodePathByLabelAndType(node.children, label, type, [...path, treeNodeExpandId(node)]);
      if (childPath.length > 0) {
        return childPath;
      }
    }
  }
  return [];
};

// Find the path to the node with the given unique ID
const findNodePathById = (nodes, targetId, path = []) => {
  for (const node of nodes) {
    if (node.id == targetId) {
      return [...path, treeNodeExpandId(node)];
    }
    if (node.children && node.children.length > 0) {
      const childPath = findNodePathById(node.children, targetId, [...path, treeNodeExpandId(node)]);
      if (childPath.length > 0) {
        return childPath;
      }
    }
  }
  return [];
};

// Tree actually rendered while searching (same shape as `filteredTreeData`).
const getFilteredTreeData = (treeData, searchQuery) => {
  const q = (searchQuery || '').trim().toLowerCase();
  if (!q) return treeData;
  // Use type comparison for internationalization support
  const allOrgsNode = treeData.find((node) => node.type === 'All Organizations');
  if (allOrgsNode && allOrgsNode.children) {
    const filteredChildren = filterTreeData(allOrgsNode.children, q);
    return filteredChildren.length > 0 ? [{ ...allOrgsNode, children: filteredChildren }] : [];
  }
  return [];
};

// Expand every branch that still exists in the filtered tree so expansion state matches rendered rows.
const collectExpandedIdsFromFilteredTree = (nodes) => {
  const ids = [];
  const walk = (arr) => {
    if (!arr) return;
    for (const node of arr) {
      if (node.children && node.children.length > 0) {
        ids.push(treeNodeExpandId(node));
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return ids;
};

// Main BorderedTreeView Component
export default function OrganizationDropdown(props) {
  const dropdownButtonRef = React.useRef(null);
  const pendingSearchUrlRef = useRef(null);
  const pendingSsidHandoffRef = useRef(null);
  // { organization, location } links from global search for organization-or-location picks
  const pendingScopedUrlsRef = useRef(null);
  const searchSelectionTypeRef = useRef('location');
  const navigate = useNavigate();
  const theme = useTheme();
  const intl = useIntl();
  const selectLocationLabel = intl.formatMessage({ id: 'selectLocation' });
  const allOrganizationsLabel = intl.formatMessage({ id: 'allOrganizations' });
  const [searchFeatureTitle, setSearchFeatureTitle] = useState('');
  const [searchSelectionType, setSearchSelectionType] = useState('location');
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [orgSelectedName, setOrgSelectedName] = useState(props.organizationName?.label);
  const [addDeviceSelectedNameAllOrg, setaddDeviceSelectedNameAllOrg] = useState(
    props.calledFrom === 'addDeviceAllOrg'
      ? (props.addDeviceAllOrgLocId ? props.addDeviceAllOrgLocName : selectLocationLabel)
      : (props.addDeviceAllOrgLocName || selectLocationLabel)
  );
  const [expandedItems, setExpandedItems] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null); // Track selected node
  const [treeFooterError, setTreeFooterError] = useState(false); // Error message to show in the footer of the tree
  const {treeData } = props;
  // Defer heavy filter + re-render while typing so large trees stay responsive (input stays immediate).
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const filteredTreeData = useMemo(
    () => getFilteredTreeData(treeData, deferredSearchQuery),
    [treeData, deferredSearchQuery],
  );
  const deferredSearchTrimmed = (deferredSearchQuery || '').trim();
  const selectedScopeType = localStorage.getItem('scopeId') == 1 ? 'Org' : localStorage.getItem('scopeId') == 2 ? 'Loc' : localStorage.getItem('scopeId') == 3 ? 'CustomGroup' : undefined;
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { mode } = useConfig();
  const isDarkMode = theme.palette.mode === 'dark';
  const toggleExpandedItem = useCallback((itemId) => {
    const id = String(itemId);
    setExpandedItems((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const next = new Set(prevArr.map(String));
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return Array.from(next);
    });
  }, []);

  const expandTreeItem = useCallback(
    (data, defaultName, desiredType) => {
      if (anchorEl) {
        // Use type comparison for internationalization support
        const allOrgsNode = treeData.find((node) => node.type === 'All Organizations');
        const rootId = allOrgsNode
          ? treeNodeExpandId(allOrgsNode)
          : Array.isArray(treeData) && treeData.length > 0
            ? treeNodeExpandId(treeData[0])
            : undefined;

        if (data) {
          let path = [];
          const useIdForPath = selectedNodeId && selectedNodeId !== 'Organization-wide' && selectedNodeId !== 'Select Location';
          if (useIdForPath) {
            path = findNodePathById(treeData, selectedNodeId);
          }
          if (path.length === 0) {
            path = desiredType ? findNodePathByLabelAndType(treeData, data, desiredType) : findNodePath(treeData, data);
          }
          const ancestorsOnly = path.length > 1 ? path.slice(0, -1) : [];

          if (data == defaultName) {
            setExpandedItems(rootId != null ? toExpandedItemIds([rootId]) : []);
          } else {
            setExpandedItems(
              ancestorsOnly.length > 0 ? toExpandedItemIds(ancestorsOnly) : rootId != null ? toExpandedItemIds([rootId]) : [],
            );
          }
        } else {
          setExpandedItems(rootId != null ? toExpandedItemIds([rootId]) : []);
        }
      } else {
        setExpandedItems([]);
      }
    },
    [anchorEl, treeData, selectedNodeId],
  );

  const isHierarchyTourStepActive = useCallback(() => {
    try {
      return (
        sessionStorage.getItem('dashboardTourRunning') === '1' &&
        sessionStorage.getItem('dashboardTourStep') === '0'
      );
    } catch (_) {
      return false;
    }
  }, []);

  const handleClickAway = useCallback(() => {
    if (isHierarchyTourStepActive()) {
      return;
    }

    setAnchorEl(null);
    setSearchQuery('');
    setTreeFooterError(false);
    setSearchFeatureTitle('');
    setSearchSelectionType('location');
    searchSelectionTypeRef.current = 'location';
    pendingSearchUrlRef.current = null;
    pendingSsidHandoffRef.current = null;
    pendingScopedUrlsRef.current = null;
  }, [isHierarchyTourStepActive]);

  const handleClick = useCallback(
  (event) => {
    const willOpen = !anchorEl;

    setAnchorEl(willOpen ? event.currentTarget : null);

    if (!willOpen) {
      setSearchQuery('');
      setSearchFeatureTitle('');
      setSearchSelectionType('location');
      searchSelectionTypeRef.current = 'location';
      pendingSearchUrlRef.current = null;
      pendingSsidHandoffRef.current = null;
      pendingScopedUrlsRef.current = null;
    }

    setTreeFooterError(false);
  },
  [anchorEl],
);

  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
    setTreeFooterError((prev) => (prev ? false : prev));
  }, []);

  const handleNodeClick = useCallback(
  (id, label, calledFrom, type, item, parentId) => {
    const isLocationNode = item?.type === 'Loc' || type === 'Loc';
    const isOrganizationNode = item?.type === 'Org' || type === 'Org';
    const currentSelectionType = searchSelectionTypeRef.current;
    const pendingHandoff = pendingSsidHandoffRef.current;
    
    // Determine if we should navigate based on the selection type from global search
    const shouldNavigate = 
      (currentSelectionType === 'location' && isLocationNode) ||
      (currentSelectionType === 'organization' && isOrganizationNode) ||
      (currentSelectionType === 'organizationOrLocation' && (isOrganizationNode || isLocationNode));
    
    // Resolve the pending URL with the selected organization's name if needed
    let pendingTargetUrl = null;
    let resolvedSsidHandoff = pendingHandoff;

    if (shouldNavigate) {
      if (currentSelectionType === 'organizationOrLocation' && pendingScopedUrlsRef.current) {
        // SSID sub-features and Schedules: open the page for the picked scope
        const scopedUrls = pendingScopedUrlsRef.current;
        let ssidScope = null;
        if (isOrganizationNode && scopedUrls.organization) {
          const selectedOrgName = label || item?.label || '';
          pendingTargetUrl = scopedUrls.organization.replace(/\{orgName\}/g, encodeURIComponent(selectedOrgName));
          ssidScope = 'organization';
        } else if (isLocationNode && scopedUrls.location) {
          pendingTargetUrl = scopedUrls.location;
          ssidScope = 'network';
        }
        resolvedSsidHandoff =
          pendingHandoff && ssidScope
            ? {
                featureTitle: pendingHandoff.featureTitle,
                subFeatureName: pendingHandoff.subFeatureName || '',
                ssidScope
              }
            : null;
      } else if (pendingSearchUrlRef.current) {
        let url = pendingSearchUrlRef.current;
        // For organization selection, resolve {orgName} placeholder with the selected org's label
        if (currentSelectionType === 'organization' && isOrganizationNode && url.includes('{orgName}')) {
          const selectedOrgName = label || item?.label || '';
          url = url.replace(/\{orgName\}/g, encodeURIComponent(selectedOrgName));
        }
        pendingTargetUrl = url;
      }
    }

    if (props.calledFrom === 'addDeviceAllOrg') {
      if (item.type === 'Loc') {
        setTreeFooterError(false);
        props.selectedLocationforAllOrg(item);

        pendingSearchUrlRef.current = null;
        searchSelectionTypeRef.current = 'location';
        handleClickAway();

        if (pendingTargetUrl) {
          navigate(pendingTargetUrl);
        }
        return;
      }

      if (item?.type === 'Org') {
        if (Array.isArray(item?.children) && item.children.length > 0) {
          setTreeFooterError(false);
          toggleExpandedItem(treeNodeExpandId(item));
        } else {
          setTreeFooterError(true);
        }
      }

      return;
    }

    props.selectedOrg(id, label, type, item, parentId);

    const ssidHandoff = resolvedSsidHandoff;
    pendingSearchUrlRef.current = null;
    pendingSsidHandoffRef.current = null;
    pendingScopedUrlsRef.current = null;
    searchSelectionTypeRef.current = 'location';
    handleClickAway();

    if (pendingTargetUrl) {
      if (ssidHandoff) {
        setGlobalSearchSsidHandoff({ ...ssidHandoff, showPagePrompt: true });
      }
      navigate(pendingTargetUrl);
    }
  },
  [
    props.calledFrom,
    props.selectedLocationforAllOrg,
    props.selectedOrg,
    handleClickAway,
    toggleExpandedItem,
    navigate
  ]
);

  useEffect(() => {
    setOrgSelectedName(props.organizationName?.label);
    if (props.calledFrom === 'addDeviceAllOrg') {
      setaddDeviceSelectedNameAllOrg(
        props.addDeviceAllOrgLocId ? props.addDeviceAllOrgLocName : selectLocationLabel
      );
    } else {
      setaddDeviceSelectedNameAllOrg(props.addDeviceAllOrgLocName);
    }
    // Initialize selectedNodeId from props (ID-based avoids wrong selection when org/loc names are duplicated)
    if (props.calledFrom === "addDeviceAllOrg") {
      setSelectedNodeId(props.addDeviceAllOrgLocId != null && props.addDeviceAllOrgLocId !== "" ? props.addDeviceAllOrgLocId : null);
    } else if (props.organizationName?.id) {
      setSelectedNodeId(props.organizationName.id);
    }
  }, [props.organizationName?.label, props.organizationName?.id, props.organizationName?.type, props.addDeviceAllOrgLocName, props.calledFrom, props.addDeviceAllOrgLocId, selectLocationLabel]);

  useEffect(() => {
    if ((searchQuery || '').trim()) return;
    expandTreeItem(props.calledFrom == "addDeviceAllOrg" ? addDeviceSelectedNameAllOrg : orgSelectedName, props.calledFrom == "addDeviceAllOrg" ? selectLocationLabel : allOrganizationsLabel, selectedScopeType);
  }, [
    anchorEl,
    treeData,
    orgSelectedName,
    addDeviceSelectedNameAllOrg,
    selectedNodeId,
    searchQuery,
    expandTreeItem,
    selectedScopeType,
    props.calledFrom,
    selectLocationLabel,
    allOrganizationsLabel,
  ]);

  // Search: expand all branches in the filtered result so virtual rows match the narrowed tree.
  // Gate on deferred query so we never expand from a full tree while the typed query hasn’t been applied yet.
  useLayoutEffect(() => {
    if (!anchorEl) return;
    if (!deferredSearchTrimmed) return;
    setExpandedItems(collectExpandedIdsFromFilteredTree(filteredTreeData));
  }, [anchorEl, filteredTreeData, deferredSearchTrimmed]);

  useEffect(() => {
    const hasVerticalScrollbar = document.body.scrollHeight > window.innerHeight;

    const ua = navigator.userAgent;
    const isFirefox = /Firefox/.test(ua);

    if (anchorEl && hasVerticalScrollbar && !isFirefox) {
      document.body.classList.add('dropdown-open');
    } else {
      document.body.classList.remove('dropdown-open');
    }

    return () => {
      document.body.classList.remove('dropdown-open');
    };
  }, [anchorEl]);

  // Listen for search-triggered tree opening events from the global search component. When received, open the dropdown and set the title based on the event detail (which includes the feature that was searched for).
  useEffect(() => {
    const handleOpenTree = (event) => {
      const selectionType = event.detail?.selectionType || 'location';
      pendingSearchUrlRef.current = event.detail?.targetUrl || null;
      pendingSsidHandoffRef.current = event.detail?.ssidHandoff || null;
      pendingScopedUrlsRef.current = event.detail?.scopedTargetUrls || null;
      setSearchFeatureTitle(event.detail?.featureTitle || '');
      setSearchSelectionType(selectionType);
      searchSelectionTypeRef.current = selectionType;

      if (dropdownButtonRef.current) {
        setAnchorEl(dropdownButtonRef.current);
        setTreeFooterError(false);
      }
    };

    window.addEventListener('OPEN_ORG_TREE_FROM_SEARCH', handleOpenTree);

    return () => {
      window.removeEventListener('OPEN_ORG_TREE_FROM_SEARCH', handleOpenTree);
    };
  }, []);

  // Determine if Popper is open based on anchorEl
  const open = Boolean(anchorEl);

  return (
    <Box
      sx={{
        marginTop: '10px',
        marginBottom: '10px',
        position: 'relative',
        ...(!drawerOpen && {
           marginBottom: '20px',
        })
      }}
      id={`org-dropdown-box-${props.calledFrom}`}
    >
      {/* Label positioned at top-left of the border */}
      {props.calledFrom !== "addDeviceAllOrg" && drawerOpen && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            top: -8,
            left: 8,
            backgroundColor: theme.palette.mode === 'dark' ? '#181A1F' : theme.palette.background.paper,
            px: 1,
            py: 0.2,
            color: 'grey.600',
            zIndex: 1,
            lineHeight: '14px',
          }}
        >
          <FormattedMessage
            id={props.calledFrom === 'Org' ? 'hierarchyTree' : 'locationTree'}
          />
        </Typography>
      )}
      <Tooltip title={orgSelectedName}
        placement="right"
        arrow
        disableHoverListener={drawerOpen}
        disableFocusListener={drawerOpen}
        disableTouchListener={drawerOpen}
        disableInteractive
      >
        <Box
          ref={dropdownButtonRef}
          onClick={handleClick}
          aria-describedby={`org-tree-popper-${props.calledFrom}`}
          id={`org-dropdown-button-${props.calledFrom}`}
          sx={{
            border: '1px solid',
            borderColor: 'grey.900',
            borderRadius: '8px',
            padding: '13px 15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',

            '&:hover': {
              borderColor: 'primary.main',
            },
            color: 'grey.900',
            ...(!drawerOpen && {
              border: 'none',
              backgroundColor: mode === ThemeMode.DARK ? alpha(theme.palette.white.paper, 0.08) : 'grey.200',
              width: '42px',
              height: '42px',
              padding: '5px 3px',
              marginLeft: '15px',
              position: 'relative',
            }),
            ...(props.calledFrom === "addDeviceAllOrg" && {
                width: '250px',
                backgroundColor: 'transparent',
                border: '1px solid',
                marginLeft: 0,
                '& .org-dropdown-avatar-box':{
                  display: 'none',
                }
            })
          }}
          tabIndex={0}
        >
          {props.calledFrom == "addDeviceAllOrg" &&
            <Box sx={{ flex: 1 }}>
              <TruncateTooltip
                placement="bottom"
                variant="body2"
                id={`org-dropdown-default-value-${props.calledFrom}`}
                sx={{
                  whiteSpace: 'pre',
                  maxWidth: '207px',
                }}
                >
                {addDeviceSelectedNameAllOrg}
              </TruncateTooltip>
            </Box>
          }
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} className='org-dropdown-avatar-box'>
            <Avatar
              sx={{
                bgcolor: 'transparent',
                color: 'grey.900',
                border: 'none',
                width: '20px',
                height: '20px',
                ...(!drawerOpen && {
                  width: '21px',
                  ml: '6px',
                })
              }}
              id={`org-dropdown-avatar-${props.calledFrom}`}
            >
              {props.organizationName?.icon == "All Organizations" ? <IconWorld sx={{ width: '18px' }} stroke={1.25} /> : props.organizationName?.icon == "Org" ? <Box component="img" src={theme.palette.mode === 'dark' ? DarkIcon : LightIcon} alt="Building icon" sx={{ width: '17px', ...(!drawerOpen && { width: '19px' }) }} /> : (props.calledFrom == "addDeviceAllOrg" || (props.organizationName?.icon == "Device")) ? " " : (props.organizationName?.icon == "CustomGroup") ? <Box component="img" className={isDarkMode ? 'img-dark' : ''} src={DeviceGroupIcon} alt="Device group 1" sx={{ width: '15px' }} /> : <LocationOnOutlinedIcon sx={{ width: '18px', ...(!drawerOpen && { width: '22px' }) }} />}
            </Avatar>

            {drawerOpen && (
              <TruncateTooltip
                placement="bottom"
                variant="h5"
                sx={{
                  color: 'grey.900',
                  whiteSpace: 'pre',
                }}
                id={`org-dropdown-truncate-tooltip-${props.calledFrom}`}>
                {orgSelectedName}
              </TruncateTooltip>
            )}
          </Box>
          <Box sx={{
            height: '13px',
            ...(!drawerOpen && {
              position: 'absolute',
              right: '-1px',
              height: '15px',
            }),
            ...(props.calledFrom === "addDeviceAllOrg" && {
                right: '10px'
            })
          }}>
            {open ? (
              <IconChevronUp stroke={1.5} size={16} />
            ) : (
              <IconChevronDown stroke={1.5} size={16} />
            )}
          </Box>
        </Box>
      </Tooltip>
      <Popper
        id={`org-tree-popper-${props.calledFrom}1`}
        open={open}
        className='bodyhasMenudropdown'
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{
          height: 'calc(100% - 205px)',
          maxHeight: 'calc(100% - 205px)',
          zIndex: 9991,
        }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            sx={{
              minWidth: 350,
              borderBottomRightRadius: '8px',
              borderBottomLeftRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
            id={`org-dropdown-paper-${props.calledFrom}`}
          >
            {props.showLoaderValue && <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 10,
                backgroundColor: 'rgba(224, 224, 224, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              id="commonLoader"
            >
              <CircularProgress sx={{ color: '#1a223f' }} />
            </Box>}
            {/*  Show search guidance alert when there's a search feature title from the global search */}
            {searchFeatureTitle && (
              <Alert
                severity="warning"
                variant='filled'
                icon={
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 20,
                    }}
                  />
                }
                sx={{
                  mx: 3,
                  mt: 2,
                  mb: -1,
                  px: 1,
                  fontSize: '13px',
                  fontWeight: 400,
                  '& .MuiAlert-icon': {
                    mr: 0.5,
                  }
                }}
              >
                <FormattedMessage
                  id={
                    searchSelectionType === 'organization'
                      ? 'selectOrganizationToAccessFeature'
                      : searchSelectionType === 'organizationOrLocation'
                        ? 'selectOrganizationOrLocationToAccessFeature'
                        : 'selectLocationToAccessFeature'
                  }
                  defaultMessage={
                    searchSelectionType === 'organization'
                      ? 'To access {feature}, select an organization'
                      : searchSelectionType === 'organizationOrLocation'
                        ? 'To access {feature}, select an organization/location'
                        : 'To access {feature}, select a location'
                  }
                  values={{
                    feature: (
                      <Typography
                        component="span"
                        sx={{ fontWeight: 500, fontSize: '13px' }}
                      >
                        {searchFeatureTitle}
                      </Typography>
                    )
                  }}
                />
              </Alert>
            )}
<TextField
                              variant="outlined"
                              placeholder={intl.formatMessage({ id: 'hierarchyTreeSearchPlaceholder' })}
                              value={searchQuery}
                              onChange={handleSearchChange}
              size="small"
              fullWidth
              id={`org-dropdown-search-textfield-${props.calledFrom}`}
              className='inputBackTransparent'
              sx={{
                padding: '26px 24px 16px 24px',
                minWidth: '100%',
                '& .MuiInputBase-input': {
                  fontWeight: 400, // Lighter font weight for input text
                },
                '& .MuiInputBase-input::placeholder': {
                  // Lighter font weight for placeholder
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            {props.calledFrom != "addDeviceAllOrg" &&
              <Box sx={{ position: 'relative' }}>
                <IconButton title='Reload' id='reloadButton' onClick={() => props.onOpen()}
                  sx={{
                    color: 'grey.800',
                    border: 'solid 1px',
                    borderColor: 'divider',
                    padding: '4px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    position: 'absolute',
                    top: '9px',
                    right: '24px',
                    zIndex: 1,
                  }}
                >
                  <IconReload size={18} stroke={1.5} />
                </IconButton>
              </Box>
            }
            <Box sx={{ flex: 1, minHeight: 0, pb: props.calledFrom === "addDeviceAllOrg" && treeFooterError ? 0 : 3 }}>
              {filteredTreeData.length > 0 ? (
                <VirtualOrgHierarchyTree
                  roots={filteredTreeData}
                  expandedItems={expandedItems}
                  toggleExpandedItem={toggleExpandedItem}
                  handleNodeClick={handleNodeClick}
                  calledFrom={props.calledFrom}
                  selectedNodeId={selectedNodeId}
                  selectedValue={props.calledFrom == 'addDeviceAllOrg' ? addDeviceSelectedNameAllOrg : orgSelectedName}
                  selectedType={selectedScopeType}
                  listId={`org-dropdown-treeview-${props.calledFrom}`}
                  reserveFooterPadding={props.calledFrom === 'addDeviceAllOrg' && treeFooterError}
                />
              ) : (
                deferredSearchTrimmed && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    height: 'calc(100% - 105px)',
                      px: 3,
                      py: 4,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        textAlign: 'center',
                      }}
                    >
                      <FormattedMessage id="noResultsFound" />
                    </Typography>
                  </Box>
                )
              )}
            </Box>
            {props.calledFrom == "addDeviceAllOrg" && treeFooterError && (
              <Box
                sx={{
                  px: 3,
                  py: 1.75,
                  borderTop: '1px solid',
                  borderColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 55,
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                    textAlign: 'start',
                  }}
                  id="org-dropdown-footer-error"
                >
                  <Alert severity="error" ><FormattedMessage id="noLocationsUnderOrgSelection0" /><br/><FormattedMessage id="noLocationsUnderOrgSelection1"/></Alert>
                </Box>
              </Box>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}